import cx from "classnames"
import { Circle } from "lucide-react"
import { automaticProjectColor, projectIdentity, type ProjectIdentityFields } from "../../projectAppearance"

export const ProjectIcon = ({
    projectId,
    projectName,
    color,
    glyph,
    projectChars,
    projectEmoji,
    nativeTitle = true,
    variant = "tile",
    className,
    showColorDot = false,
}: ProjectIdentityFields & {
    projectId: string
    projectName: string
    color?: string
    glyph?: string
    nativeTitle?: boolean
    variant?: "glyph" | "tile"
    className?: string
    showColorDot?: boolean
}) => {
    const resolvedColor = color ?? automaticProjectColor(projectId)
    const identity = projectIdentity({ name: projectName, glyph, projectChars, projectEmoji })
    const showEmoji = variant === "glyph" && !!identity.projectEmoji
    return (
        <span
            data-project-color={projectId}
            data-workspace-color={projectId}
            className={cx(
                variant === "glyph"
                    ? "relative inline-flex h-5 w-5 flex-none items-center justify-center leading-none"
                    : "inline-flex h-5 w-5 flex-none items-center justify-center rounded-[3px] border leading-none",
                className
            )}
            style={
                variant === "glyph"
                    ? { color: resolvedColor }
                    : {
                          backgroundColor: `color-mix(in srgb, ${resolvedColor} 8%, transparent)`,
                          borderColor: `color-mix(in srgb, ${resolvedColor} 26%, transparent)`,
                          color: resolvedColor,
                      }
            }
            title={nativeTitle ? projectName : undefined}
            aria-label={`Project ${projectName}`}
        >
            <span
                aria-hidden="true"
                className={
                    showEmoji
                        ? "text-[18px]"
                        : variant === "glyph"
                          ? "text-[12px] font-semibold tracking-[0.02em]"
                          : "text-[10px] font-semibold tracking-[0.01em]"
                }
                style={showEmoji ? { fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' } : undefined}
            >
                {showEmoji ? identity.projectEmoji : identity.projectChars}
            </span>
            {showColorDot && showEmoji ? <Circle aria-hidden="true" className="absolute -right-0.5 bottom-0 h-1.5 w-1.5 fill-current" strokeWidth={0} /> : null}
        </span>
    )
}
